<template>
  <div class="cs5-wrap">
    <div class="cs5-form-card">
      <div class="cs5-content-scroll">
        <div v-if="loading" class="cs5-loading">
          불러오는 중...
        </div>

        <template v-else>
          <div class="cs5-summary-top">
            <div class="cs5-info-section">
              <div class="cs5-info-row">
                <div class="cs5-info-label">회사명</div>
                <div class="cs5-info-value">{{ resolvedAgencyName || "-" }}</div>
              </div>

              <div class="cs5-info-row">
                <div class="cs5-info-label">발주처명</div>
                <div class="cs5-info-value">{{ resolvedClientName || "-" }}</div>
              </div>

              <div class="cs5-info-row">
                <div class="cs5-info-label">공사명</div>
                <div class="cs5-info-value">{{ detail.siteName || "-" }}</div>
              </div>

              <div class="cs5-info-row">
                <div class="cs5-info-label">주소</div>
                <div class="cs5-info-value">{{ fullAddress || "-" }}</div>
              </div>

              <div class="cs5-info-row">
                <div class="cs5-info-label">상태</div>
                <div class="cs5-info-value">{{ detail.status || "-" }}</div>
              </div>
            </div>

            <div class="cs5-drawing-panel">
              <div class="cs5-drawing-box">
                <div
                  v-if="drawingPreviewUrl && drawingRenderWidth > 0 && drawingRenderHeight > 0"
                  class="cs5-drawing-stage"
                >
                  <div
                    class="cs5-image-box"
                    :style="drawingImageBoxStyle"
                  >
                    <img
                      :src="drawingPreviewUrl"
                      class="cs5-drawing-image"
                      alt="현장 도면"
                      @load="onDrawingImageLoad"
                      draggable="false"
                    />

                    <div
                      v-for="logger in resolvedLoggerItems"
                      :key="logger.id"
                      class="cs5-logger-marker"
                      :style="getLoggerStyle(logger)"
                    >
                      <div class="cs5-logger-dot"></div>
                      <div class="cs5-logger-label">
                        {{ logger.label }}
                      </div>
                    </div>
                  </div>
                </div>

                <img
                  v-else-if="drawingPreviewUrl"
                  :src="drawingPreviewUrl"
                  class="cs5-hidden-measure-image"
                  alt="현장 도면"
                  @load="onDrawingImageLoad"
                />

                <div v-else class="cs5-drawing-empty">
                  도면 이미지가 없습니다.
                </div>
              </div>
            </div>
          </div>

          <div class="cs5-table-wrap">
            <table class="cs5-table">
              <thead>
                <tr>
                  <th>로거명</th>
                  <th>모델명</th>
                  <th>Part Name</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="logger in detail.loggerItems"
                  :key="`row_${logger.sensorLoggerSiteId || logger.sensorLoggerId}`"
                >
                  <td>{{ logger.loggerType || "-" }}</td>
                  <td>{{ logger.modelName || "-" }}</td>
                  <td>{{ logger.partName || logger.loggerName || "-" }}</td>
                </tr>

                <tr v-if="!detail.loggerItems.length">
                  <td colspan="3" class="cs5-empty-cell">
                    등록된 로거가 없습니다.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="cs5-finish-text">
            {{
              isEditMode
                ? `위와 같이 (${detail.siteName || "공사명"}) 현장 수정 내용을 확인합니다.`
                : `위와 같이 (${detail.siteName || "공사명"}) 신규 현장 등록을 완료합니다.`
            }}
          </div>
        </template>
      </div>

      <div class="cs5-bottom-actions">
        <button class="cs5-btn cs5-btn-secondary" type="button" @click="onBack">
          이전으로
        </button>

        <button
          class="cs5-btn cs5-btn-primary"
          type="button"
          @click="onComplete"
          :disabled="loading || completing"
        >
          {{ completeButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios'

export default {
  name: "CreateSiteStep5",

  props: {
    mode: {
      type: String,
      default: "create",
    },
    formValue: {
      type: Object,
      default() {
        return {}
      },
    },
  },

  data() {
    return {
      loading: false,
      completing: false,
      detail: {
        siteId: null,
        siteName: "",
        agencyName: "",
        clientName: "",
        address: "",
        addressDetail: "",
        drawingAttachFileNo: null,
        status: "",
        loggerItems: [],
      },

      drawingPreviewUrl: "",
      localDrawingObjectUrl: null,

      drawingNaturalWidth: 0,
      drawingNaturalHeight: 0,
      resizeTick: 0,
    }
  },

  computed: {
    isEditMode() {
      return this.mode === "edit"
    },

    completeButtonText() {
      if (this.isEditMode) {
        return this.detail.status === "ACTIVE" ? "수정 완료" : "수정 후 활성화"
      }
      return "완료"
    },

    resolvedAgencyName() {
      return this.detail.agencyName || this.formValue?.agencyName || ""
    },

    resolvedClientName() {
      return this.detail.clientName || this.formValue?.clientName || ""
    },

    fullAddress() {
      const address = this.detail.address || ""
      const addressDetail = this.detail.addressDetail || ""

      if (address && addressDetail) {
        return `${address} ${addressDetail}`
      }

      return address || addressDetail || ""
    },

    drawingScale() {
      const _tick = this.resizeTick
      void _tick

      if (!this.drawingNaturalWidth || !this.drawingNaturalHeight) {
        return 1
      }

      const maxWidth = 520
      const maxHeight = 520

      const scaleX = maxWidth / this.drawingNaturalWidth
      const scaleY = maxHeight / this.drawingNaturalHeight

      return Math.min(scaleX, scaleY, 1)
    },

    drawingRenderWidth() {
      return Math.round(this.drawingNaturalWidth * this.drawingScale)
    },

    drawingRenderHeight() {
      return Math.round(this.drawingNaturalHeight * this.drawingScale)
    },

    drawingImageBoxStyle() {
      return {
        width: `${this.drawingRenderWidth}px`,
        height: `${this.drawingRenderHeight}px`,
      }
    },

    /**
     * 핵심:
     * 1) Step3에서 넘어온 formValue.loggers가 있으면 그걸 최우선 사용
     * 2) 없으면 서버 step5/detail loggerItems를 fallback으로 사용
     */
    resolvedLoggerItems() {
      const fromStep3 = Array.isArray(this.formValue?.loggers)
        ? this.formValue.loggers
        : []

      if (fromStep3.length > 0) {
        return fromStep3.map((logger, index) => ({
          id:
            logger.sensorLoggerSiteId ||
            logger.id ||
            logger.sensorLoggerId ||
            `wizard_logger_${index}`,
          label: logger.name || '-',
          leftPx: Number(logger.x ?? 0) * this.drawingScale,
          topPx: Number(logger.y ?? 0) * this.drawingScale,
        }))
      }

      const fromServer = Array.isArray(this.detail?.loggerItems)
        ? this.detail.loggerItems
        : []

      return fromServer.map((logger, index) => {
        const naturalX = this.normalizeServerPoint(
          logger.positionX,
          this.drawingNaturalWidth
        )
        const naturalY = this.normalizeServerPoint(
          logger.positionY,
          this.drawingNaturalHeight
        )

        return {
          id:
            logger.sensorLoggerSiteId ||
            logger.sensorLoggerId ||
            `server_logger_${index}`,
          label: logger.partName || logger.loggerName || '-',
          leftPx: naturalX * this.drawingScale,
          topPx: naturalY * this.drawingScale,
        }
      })
    },
  },

  mounted() {
    this.init()
    window.addEventListener("resize", this.handleResize)
  },

  watch: {
    formValue: {
      deep: true,
      handler() {
        this.init()
      },
    },
  },

  beforeUnmount() {
    this.revokeLocalDrawingObjectUrl()
    window.removeEventListener("resize", this.handleResize)
  },

  methods: {
    async init() {
      if (!this.formValue?.siteId) return

      this.restoreDrawingPreviewFromForm()
      await this.loadStep5Detail()
      this.loadDrawingNaturalSize()
    },

    handleResize() {
      this.resizeTick++
    },

    onDrawingImageLoad(event) {
      const img = event.target
      this.drawingNaturalWidth = img.naturalWidth || 0
      this.drawingNaturalHeight = img.naturalHeight || 0
      this.resizeTick++
    },

    async loadDrawingNaturalSize() {
      if (!this.drawingPreviewUrl) return

      const img = new Image()
      img.onload = () => {
        this.drawingNaturalWidth = img.naturalWidth || 0
        this.drawingNaturalHeight = img.naturalHeight || 0
        this.resizeTick++
      }
      img.src = this.drawingPreviewUrl
    },

    revokeLocalDrawingObjectUrl() {
      if (this.localDrawingObjectUrl) {
        URL.revokeObjectURL(this.localDrawingObjectUrl)
        this.localDrawingObjectUrl = null
      }
    },

    restoreDrawingPreviewFromForm() {
      this.revokeLocalDrawingObjectUrl()

      if (this.formValue?.drawing instanceof File) {
        this.localDrawingObjectUrl = URL.createObjectURL(this.formValue.drawing)
        this.drawingPreviewUrl = this.localDrawingObjectUrl
        return
      }

      if (this.formValue?.drawingAttachFileNo) {
        this.drawingPreviewUrl = `/app/file/image/${this.formValue.drawingAttachFileNo}`
        return
      }

      this.drawingPreviewUrl = ""
    },

    async loadStep5Detail() {
      if (!this.formValue?.siteId) return

      try {
        this.loading = true

        const res = await api.get("/app/site/step5/detail", {
          params: {
            siteId: this.formValue.siteId,
          },
        })

        const result = res.data?.result

        if (!result) {
          this.detail = {
            siteId: this.formValue.siteId,
            siteName: "",
            agencyName: this.formValue?.agencyName || "",
            clientName: this.formValue?.clientName || "",
            address: "",
            addressDetail: "",
            drawingAttachFileNo: null,
            status: "",
            loggerItems: [],
          }
          return
        }

        this.detail = {
          siteId: result.siteId ?? null,
          siteName: result.siteName ?? "",
          agencyName: result.agencyName ?? this.formValue?.agencyName ?? "",
          clientName: result.clientName ?? this.formValue?.clientName ?? "",
          address: result.address ?? "",
          addressDetail: result.addressDetail ?? this.formValue?.addressDetail ?? "",
          drawingAttachFileNo: result.drawingAttachFileNo ?? null,
          status: result.status ?? "",
          loggerItems: Array.isArray(result.loggerItems) ? result.loggerItems : [],
        }

        if (!(this.formValue?.drawing instanceof File) && this.detail.drawingAttachFileNo) {
          this.drawingPreviewUrl = `/app/file/image/${this.detail.drawingAttachFileNo}`
        }

        this.$nextTick(() => {
          this.loadDrawingNaturalSize()
        })
      } catch (e) {
        console.error("step5 detail load failed", e)
        console.error("status =", e?.response?.status)
        console.error("data =", e?.response?.data)
        console.error("siteId =", this.formValue?.siteId)
        alert("완료 화면 조회 실패")
      } finally {
        this.loading = false
      }
    },

    normalizeServerPoint(value, fullSize) {
      const num = Number(value ?? 0)

      if (!Number.isFinite(num)) return 0

      if (num >= 0 && num <= 100) {
        return Math.round((num / 100) * fullSize)
      }

      return Math.round(num)
    },

    getLoggerStyle(logger) {
      return {
        left: `${logger.leftPx}px`,
        top: `${logger.topPx}px`,
      }
    },

    onBack() {
      this.$emit("back", {
        doneFlag: false,
      })
    },

    async onComplete() {
      if (!this.formValue?.siteId) {
        alert("siteId가 없습니다.")
        return
      }

      try {
        this.completing = true

        await api.post("/app/site/step5/complete", {
          id: this.formValue.siteId,
        })

        alert(
          this.mode === "edit"
            ? "현장 수정이 완료되었습니다."
            : "신규 현장 등록이 완료되었습니다."
        )

        this.$router.push({
          path: "/admin",
          query: {
            menu: "siteList",
          },
        })
      } catch (e) {
        console.error("step5 complete failed", e)
        alert(
          this.mode === "edit"
            ? "현장 수정 완료 처리 실패"
            : "현장 완료 처리 실패"
        )
      } finally {
        this.completing = false
      }
    },
  },
}
</script>

<style scoped>
.cs5-wrap {
  background: #ffffff;
  padding: 10px 22px 28px;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.cs5-form-card {
  margin-top: 12px;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.cs5-content-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-right: 4px;
}

.cs5-loading {
  min-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 16px;
  font-weight: 600;
}

.cs5-summary-top {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 560px;
  gap: 32px;
  align-items: start;
  margin-bottom: 24px;
}

.cs5-info-section {
  padding: 28px 8px 0 8px;
}

.cs5-info-row {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr);
  column-gap: 18px;
  align-items: start;
  margin-bottom: 30px;
}

.cs5-info-label {
  font-size: 18px;
  font-weight: 700;
  color: #374151;
}

.cs5-info-value {
  font-size: 18px;
  font-weight: 600;
  color: #ef4444;
  line-height: 1.5;
  word-break: break-all;
}

.cs5-drawing-panel {
  display: flex;
  justify-content: flex-end;
}

.cs5-drawing-box {
  width: 100%;
  max-width: 560px;
  min-height: 560px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  border-radius: 8px;
  overflow: auto;
  padding: 18px;
  box-sizing: border-box;
}

.cs5-drawing-stage {
  width: 100%;
  min-width: max-content;
  min-height: 100%;
  text-align: center;
}

.cs5-image-box {
  position: relative;
  display: inline-block;
  vertical-align: top;
}

.cs5-drawing-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: fill;
  user-select: none;
  pointer-events: none;
}

.cs5-hidden-measure-image {
  opacity: 0;
  pointer-events: none;
  position: absolute;
}

.cs5-drawing-empty {
  width: 100%;
  min-height: 520px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
}

.cs5-logger-marker {
  position: absolute;
  z-index: 20;
  transform: translate(-50%, -100%);
  pointer-events: none;
}

.cs5-logger-dot {
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: #ef4444;
  border: 2px solid #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  margin: 0 auto;
}

.cs5-logger-label {
  margin-top: 3px;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.88);
  color: #ffffff;
  font-size: 0.8rem;
  line-height: 1.2;
  white-space: nowrap;
}

.cs5-table-wrap {
  margin-top: 10px;
  overflow-x: auto;
}

.cs5-table {
  width: 100%;
  min-width: 780px;
  border-collapse: collapse;
  table-layout: fixed;
  background: #ffffff;
}

.cs5-table th,
.cs5-table td {
  border: 1px solid #31456a;
  padding: 14px 12px;
  text-align: center;
  vertical-align: middle;
  font-size: 15px;
}

.cs5-table th {
  background: #f3f4f6;
  font-weight: 700;
  color: #111827;
}

.cs5-empty-cell {
  color: #6b7280;
  padding: 40px 0;
}

.cs5-finish-text {
  margin-top: 30px;
  text-align: center;
  font-size: 17px;
  color: #111827;
  padding-bottom: 10px;
}

.cs5-bottom-actions {
  margin-top: 14px;
  padding-top: 18px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;
  background: #ffffff;
}

.cs5-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  font-family: inherit;
}

.cs5-btn:hover {
  background: #f3f4f6;
}

.cs5-btn-primary {
  background: var(--color-primary);
  border-color: var(--color-primary);
  color: #ffffff;
}

.cs5-btn-primary:hover {
  background: var(--color-primary-border);
  border-color: var(--color-primary-border);
}

.cs5-btn-secondary {
  background: #ffffff;
  border-color: #d1d5db;
  color: #111827;
}

.cs5-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 1400px) {
  .cs5-summary-top {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .cs5-drawing-panel {
    justify-content: flex-start;
  }

  .cs5-drawing-box {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .cs5-wrap {
    padding: 10px 16px 22px;
  }

  .cs5-info-row {
    grid-template-columns: 84px minmax(0, 1fr);
    column-gap: 14px;
    margin-bottom: 22px;
  }

  .cs5-info-label,
  .cs5-info-value {
    font-size: 15px;
  }

  .cs5-drawing-box {
    min-height: 420px;
    padding: 12px;
  }

  .cs5-finish-text {
    font-size: 15px;
  }
}
</style>